import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import { NotebookPen, Save, Loader2, CheckSquare, List, Hash, Circle } from 'lucide-react';

const PersonalNotes = () => {
  const [content, setContent] = useState('');
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('preview'); // 'edit' or 'preview'
  const textareaRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    loadUserAndNotes();
  }, []);

  useEffect(() => {
    if (!hasLoadedRef.current || !userId) return;
    
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(() => {
      saveNotes();
    }, 1000);

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [content, userId]);

  const loadUserAndNotes = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      if (!user) {
        toast.error('You need to be logged in');
        setLoading(false);
        return;
      }
      setUserId(user.id);
      await loadNotes(user.id);
    } catch (err) {
      console.error('Failed to load', err);
      toast.error('Could not load your notebook');
    } finally {
      setLoading(false);
    }
  };

  const loadNotes = async (uid) => {
    try {
      const { data, error } = await supabase
        .from('personal_notes')
        .select('content')
        .eq('user_id', uid)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      const loadedContent = data?.content || '';
      setContent(loadedContent);
      hasLoadedRef.current = true;

      // Focus textarea after load
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Move cursor to end
          const len = loadedContent.length;
          textareaRef.current.setSelectionRange(len, len);
        }
      }, 100);
    } catch (err) {
      console.error('Failed to fetch notes', err);
      toast.error('Could not load notes');
    }
  };

  const saveNotes = async () => {
    if (!userId) return;
    
    try {
      setSaving(true);
      const { error } = await supabase
        .from('personal_notes')
        .upsert(
          { user_id: userId, content },
          { onConflict: 'user_id' }
        );

      if (error) throw error;
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await saveNotes();
    toast.success('Notes saved!');
  };

  const insertCheckboxList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const checkboxItems = '[ ] \n[ ] \n[ ] \n[ ] \n[ ] \n';
    const cursorPos = textarea.selectionStart;
    const textBefore = content.substring(0, cursorPos);
    const textAfter = content.substring(cursorPos);
    const newContent = textBefore + (textBefore.endsWith('\n') || textBefore === '' ? '' : '\n') + checkboxItems + textAfter;
    
    setContent(newContent);
    toast('Added checkbox list');
    
    setTimeout(() => {
      textarea.focus();
      const newPos = cursorPos + (textBefore.endsWith('\n') || textBefore === '' ? 0 : 1) + 4;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const insertBulletList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const bulletItems = '• \n• \n• \n• \n• \n';
    const cursorPos = textarea.selectionStart;
    const textBefore = content.substring(0, cursorPos);
    const textAfter = content.substring(cursorPos);
    const newContent = textBefore + (textBefore.endsWith('\n') || textBefore === '' ? '' : '\n') + bulletItems + textAfter;
    
    setContent(newContent);
    toast('Added bullet list');
    
    setTimeout(() => {
      textarea.focus();
      const newPos = cursorPos + (textBefore.endsWith('\n') || textBefore === '' ? 0 : 1) + 2;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const insertNumberedList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const numberedItems = '1. \n2. \n3. \n4. \n5. \n';
    const cursorPos = textarea.selectionStart;
    const textBefore = content.substring(0, cursorPos);
    const textAfter = content.substring(cursorPos);
    const newContent = textBefore + (textBefore.endsWith('\n') || textBefore === '' ? '' : '\n') + numberedItems + textAfter;
    
    setContent(newContent);
    toast('Added numbered list');
    
    setTimeout(() => {
      textarea.focus();
      const newPos = cursorPos + (textBefore.endsWith('\n') || textBefore === '' ? 0 : 1) + 3;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const insertDivider = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const divider = '\n─────────────────────────────────────────────\n\n';
    const cursorPos = textarea.selectionStart;
    const textBefore = content.substring(0, cursorPos);
    const textAfter = content.substring(cursorPos);
    const newContent = textBefore + divider + textAfter;
    
    setContent(newContent);
    toast('Added divider');
    
    setTimeout(() => {
      textarea.focus();
      const newPos = cursorPos + divider.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const toggleCheckbox = (lineIndex) => {
    const lines = content.split('\n');
    const line = lines[lineIndex];

    if (line.includes('[ ]')) {
      lines[lineIndex] = line.replace('[ ]', '[x]');
    } else if (line.includes('[x]')) {
      lines[lineIndex] = line.replace('[x]', '[ ]');
    }

    setContent(lines.join('\n'));
  };

  const copyAllContent = () => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  const renderPreview = () => {
    const lines = content.split('\n');

    return lines.map((line, index) => {
      // Check if line contains a checkbox pattern
      const checkboxMatch = line.match(/^(\s*)\[([ x])\]\s?(.*)$/);

      if (checkboxMatch) {
        const [, indent, checked, text] = checkboxMatch;
        return (
          <div
            key={index}
            className="flex items-start gap-2 min-h-[28px] py-1"
            style={{ paddingLeft: `${indent.length * 8}px` }}
          >
            <input
              type="checkbox"
              checked={checked === 'x'}
              onChange={() => toggleCheckbox(index)}
              className="mt-1 w-4 h-4 rounded border-gray-400 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors accent-indigo-500"
            />
            <span
              className={`flex-1 text-gray-200 ${checked === 'x' ? 'line-through text-gray-500' : ''}`}
            >
              {text || '\u00A0'}
            </span>
          </div>
        );
      }

      // Regular line
      return (
        <div key={index} className="min-h-[28px] py-1 text-gray-200 whitespace-pre-wrap">
          {line || '\u00A0'}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4 text-indigo-400" size={48} />
          <p className="text-gray-400">Loading your notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0f1419]">
        <div className="flex items-center gap-3">
          <NotebookPen className="text-indigo-400" size={24} />
          <h1 className="text-2xl font-bold text-white">Personal Notes</h1>
        </div>
        <div className="flex items-center gap-3">
          {saving && (
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Saving...
            </span>
          )}
          <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1.5 rounded transition-colors text-sm ${
                viewMode === 'edit'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Edit
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1.5 rounded transition-colors text-sm ${
                viewMode === 'preview'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Preview
            </button>
          </div>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors text-white flex items-center gap-2"
          >
            <Save size={16} />
            Save Now
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 border-b border-white/10 bg-[#0f1419]/50">
        <button
          onClick={insertCheckboxList}
          className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-gray-300 text-sm flex items-center gap-2"
          title="Add Checkbox List"
        >
          <CheckSquare size={16} />
          Checkbox List
        </button>
        <button
          onClick={insertBulletList}
          className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-gray-300 text-sm flex items-center gap-2"
          title="Add Bullet List"
        >
          <List size={16} />
          Bullet List
        </button>
        <button
          onClick={insertNumberedList}
          className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-gray-300 text-sm flex items-center gap-2"
          title="Add Numbered List"
        >
          <Hash size={16} />
          Numbered List
        </button>
        <button
          onClick={insertDivider}
          className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-gray-300 text-sm flex items-center gap-2"
          title="Add Divider"
        >
          <Circle size={16} />
          Divider
        </button>
      </div>

      {/* Content Editor */}
      <div className="flex-1 overflow-auto p-4">
        {viewMode === 'edit' ? (
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Start writing your notes here...&#10;&#10;You can:&#10;• Type freely and naturally&#10;• Select multiple lines with mouse or Shift+Arrow keys&#10;• Use Ctrl+A to select all&#10;• Copy, cut, and paste as usual&#10;• Use the toolbar above to insert templates&#10;• Switch to Preview mode to see interactive checkboxes"
            className="w-full h-full bg-transparent text-white resize-none outline-none font-mono text-sm leading-relaxed placeholder-gray-600"
            spellCheck="false"
            style={{
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
              lineHeight: '1.6',
            }}
          />
        ) : (
          <div 
            className="font-mono text-sm leading-relaxed cursor-pointer" 
            onDoubleClick={() => setViewMode('edit')}
            title="Double-click to edit"
          >
            {renderPreview()}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/10 bg-[#0f1419]/50 text-xs text-gray-500 flex items-center justify-between">
        <span>
          {viewMode === 'edit' 
            ? 'Auto-saves every second after you stop typing' 
            : 'Click checkboxes to toggle • Double-click/tap to edit text'}
        </span>
        <span>{content.length} characters</span>
      </div>
    </div>
  );
};

export default PersonalNotes;
